import { KbRecordItem } from "shared";
import { dayjs } from "utilz";
import * as Y from "yjs";
import { prosemirrorJSONToYDoc } from "y-prosemirror";
import { schema } from "components/editor/Schema";
import { DOMParser as ProseMirrorDOMParser } from "prosemirror-model";
import { debugConsole } from "utilz/helpers/debugConsole";

export const generateKbFilesAndFolders = (records: any[]) => {
  const files: any[] = [];
  const folders: any[] = [];
  if (!records?.length) return { files, folders };
  records?.forEach((record: any) => {
    if (record?.kb_type === "file") {
      files.push(record);
    } else if (record?.kb_type === "folder") {
      folders.push(record);
    }
  });
  return { files, folders };
};

const filterData = ({
  filters,
  kbData,
}: {
  filters: Record<string, any>;
  kbData: KbRecordItem[];
}) => {
  if (!filters?.length) return kbData;
  if (!kbData?.length) return [];
  return kbData?.filter((item) => {
    // Filter by date_created
    if (filters.date_created?.length) {
      const dateCreatedMatch = filters.date_created.some((period: string) => {
        const itemDate = dayjs(item.created_at);
        const now = dayjs();

        switch (period) {
          case "today":
            return itemDate.isSame(now, "day");
          case "this_week":
            return itemDate.isSame(now, "week");
          case "this_month":
            return itemDate.isSame(now, "month");
          case "overall":
            return true;
          default:
            return false;
        }
      });
      if (!dateCreatedMatch) return false;
    }

    // Filter by last_modified
    if (filters.last_modified?.length) {
      const lastModifiedMatch = filters.last_modified.some((period: string) => {
        const itemDate = dayjs(item.updated_at);
        const now = dayjs();

        switch (period) {
          case "today":
            return itemDate.isSame(now, "day");
          case "this_week":
            return itemDate.isSame(now, "week");
          case "this_month":
            return itemDate.isSame(now, "month");
          case "overall":
            return true;
          default:
            return false;
        }
      });
      if (!lastModifiedMatch) return false;
    }

    // Filter by created_by
    if (filters.created_by?.length) {
      const createdByMatch = filters.created_by.includes(item.created_by);
      if (!createdByMatch) return false;
    }

    return true;
  });
};

export const getDefaultArticleContent = () => {
  const content = {
    type: "doc",
    content: [
      {
        "type": "heading",
        "attrs": {
          "textAlign": "left",
          "level": 1,
        },
        "content": [
          {
            "type": "text",
            "text": "Untitled",
          },
        ],
      },
      { type: "paragraph" },
    ],
  };

  const ydoc = prosemirrorJSONToYDoc(schema, content, "default");

  // 2) Y.Doc -> Uint8Array update
  const update: Uint8Array = Y.encodeStateAsUpdate(ydoc);

  return { update, content };
};

/**
 * Block node types that should NOT be nested inside paragraphs.
 * If found inside a paragraph, they need to be lifted to the document level.
 */
const BLOCK_NODE_TYPES = [
  "resizableImage",
  "image",
  "videoEmbed",
  "pdfEmbed",
  "imagePlaceholder",
  "fileChip",
  "table",
  "codeBlock",
  "blockquote",
  "horizontalRule",
  "heading",
  "bulletList",
  "orderedList",
  "taskList",
];

/**
 * Normalizes TipTap JSON content to fix invalid nesting.
 * Block nodes (like resizableImage) cannot be inside paragraphs.
 * This function lifts them to the document level.
 *
 * @param content - TipTap JSON content with potential invalid nesting
 * @returns Normalized content with valid ProseMirror structure
 */
const normalizeContentStructure = (content: any): any => {
  if (!content || typeof content !== "object") return content;

  // If this is not a doc node, return as-is
  if (content.type !== "doc" || !Array.isArray(content.content)) {
    return content;
  }

  const normalizedContent: any[] = [];

  for (const node of content.content) {
    // Check if this is a paragraph with block nodes inside
    if (node.type === "paragraph" && Array.isArray(node.content)) {
      const inlineContent: any[] = [];
      const extractedBlocks: any[] = [];

      for (const child of node.content) {
        if (BLOCK_NODE_TYPES.includes(child.type)) {
          // This is a block node incorrectly nested in a paragraph
          // First, save any accumulated inline content as a paragraph
          if (inlineContent.length > 0) {
            normalizedContent.push({
              type: "paragraph",
              attrs: node.attrs,
              content: [...inlineContent],
            });
            inlineContent.length = 0;
          }
          // Add the block node at document level
          extractedBlocks.push(child);
        } else {
          // Regular inline content
          inlineContent.push(child);
        }
      }

      // Add any remaining inline content
      if (inlineContent.length > 0) {
        normalizedContent.push({
          type: "paragraph",
          attrs: node.attrs,
          content: inlineContent,
        });
      } else if (extractedBlocks.length === 0) {
        // Empty paragraph, keep it
        normalizedContent.push({ type: "paragraph", attrs: node.attrs });
      }

      // Add extracted block nodes
      normalizedContent.push(...extractedBlocks);
    } else {
      // Not a paragraph or no content, keep as-is
      normalizedContent.push(node);
    }
  }

  return {
    ...content,
    content: normalizedContent,
  };
};

/**
 * Converts TipTap JSON content to a Yjs Uint8Array binary update.
 *
 * @param content - TipTap JSON content (output of editor.getJSON())
 * @param field - Yjs fragment field name (default: "default")
 * @returns Uint8Array binary state update
 */
export const tiptapJsonToYjsUpdate = (
  content: Record<string, unknown>,
  field: string = "default",
): Uint8Array => {
  // Normalize content to fix invalid nesting (block nodes inside paragraphs)
  const normalizedContent = normalizeContentStructure(content);

  debugConsole(
    "[tiptapJsonToYjsUpdate] Normalized content:",
    JSON.stringify(normalizedContent, null, 2),
  );

  const ydoc = prosemirrorJSONToYDoc(schema, normalizedContent, field);

  // Debug: Check what was created in the ydoc
  const fragment = ydoc.getXmlFragment(field);
  debugConsole(
    "[tiptapJsonToYjsUpdate] Created fragment with children:",
    fragment.toArray().length,
  );

  return Y.encodeStateAsUpdate(ydoc);
};

/**
 * Generate a consistent color for a user based on their ID
 */
export const generateUserColor = (userId: string, theme: any): string => {
  const colors = [
    theme.palette.neutral["005"],
    theme.palette.secondary["purple-tint-3"],
    theme.palette.success.tint,
    theme.palette["warning-minor"].tint,
    theme.palette.warning.tint,
    theme.palette.error.tint,
    theme.palette.primitive.system.magenta[50],
    theme.palette.primitive.brand.purple[100],
    theme.palette.primitive.system.blue[100],
  ];

  // Simple hash function to get consistent color for user
  const index =
    userId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    colors.length;

  return colors[index];
};

export { filterData };

/**
 * Converts HTML string to TipTap JSON using ProseMirror's DOMParser.
 * Uses the existing schema from Schema.ts for proper node parsing.
 * Note: ProseMirror's parser only creates valid schema nodes, providing
 * inherent protection against malformed content.
 *
 * @param html - HTML string to convert
 * @returns TipTap JSON content
 */
export const htmlToTiptapJson = (html: string): Record<string, unknown> => {
  // 1. Parse HTML using browser's DOMParser
  const domParser = new window.DOMParser();
  const dom = domParser.parseFromString(html, "text/html");

  // 2. Use ProseMirror's DOMParser with existing schema
  const pmParser = ProseMirrorDOMParser.fromSchema(schema);
  const pmNode = pmParser.parse(dom.body);

  // 3. Convert to JSON and normalize
  return normalizeContentStructure(pmNode.toJSON());
};

interface AttachmentInput {
  file_name: string;
  file_type: string;
  url: string;
}

/**
 * Creates KB article content from HTML message.
 * Converts HTML to TipTap JSON and wraps with a heading.
 *
 * @param messageHtml - HTML message content
 * @param title - Optional title for the H1 heading (defaults to "Untitled")
 * @param attachments - Optional array of attachments to include as fileChip nodes
 * @returns Object with content, yjs update, and excerpt
 */
export const createKbArticleFromHtml = (
  messageHtml: string,
  title?: string,
  attachments?: AttachmentInput[],
): {
  content: Record<string, unknown>;
  update: Uint8Array;
  excerpt: string;
} => {
  // 1. Convert HTML to TipTap JSON
  const messageJson = htmlToTiptapJson(messageHtml);

  // 2. Build content array: heading + message content
  const contentNodes: any[] = [
    {
      type: "heading",
      attrs: { textAlign: "left", level: 1 },
      content: [{ type: "text", text: title || "Untitled" }],
    },
    ...((messageJson.content as any[]) || []),
  ];

  // 3. Add attachments as fileChip nodes (each in its own paragraph)
  if (attachments && attachments.length > 0) {
    for (const attachment of attachments) {
      contentNodes.push({
        type: "paragraph",
        content: [
          {
            type: "fileChip",
            attrs: {
              type: attachment.file_type,
              name: attachment.file_name,
              src: attachment.url,
            },
          },
        ],
      });
    }
  }

  // 4. Create article structure
  const content = {
    type: "doc",
    content: contentNodes,
  };

  // 5. Generate Yjs update using existing helper
  const update = tiptapJsonToYjsUpdate(content);

  // 6. Extract text for excerpt
  const parser = new DOMParser();
  const doc = parser.parseFromString(messageHtml, "text/html");
  const excerpt = (doc.body.textContent || "").slice(0, 200);

  return { content, update, excerpt };
};

// Function to set first H1 heading as filename
export const setFirstH1AsFilename = async ({
  content,
  currentValue,
}: {
  content: Record<string, unknown>;
  currentValue: string;
}) => {
  try {
    const document = content.content as any;
    if (!document || !document.content) return;
    let heading = currentValue;
    // Find first H1 heading
    for (const node of document.content) {
      if (node.type === "heading" && node.attrs?.level === 1) {
        // Extract text from heading content
        let headingText = "";
        if (node.content) {
          headingText = node.content
            .map((textNode: any) => textNode.text || "")
            .join("")
            .trim();
        }

        if (headingText && headingText !== currentValue) {
          // methods.setValue("name", headingText, { shouldValidate: true });
          heading = headingText;
        }
        break;
      }
    }
    debugConsole("heading" + heading);
    return heading;
  } catch (error) {
    debugConsole("Error extracting H1 from content:", error);
    return currentValue;
  }
};
